import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { useCollections } from '../hooks/useCollections';
import { CollectionWithRole, CollectionUpdate } from '../types/collection';
import { DeleteCollectionDialog } from './DeleteCollectionDialog';

interface CollectionInfoTabProps {
  collection: CollectionWithRole;
  onUpdate: (collection: CollectionWithRole) => void;
  onDelete: () => void;
}

export const CollectionInfoTab: React.FC<CollectionInfoTabProps> = ({
  collection,
  onUpdate,
  onDelete
}) => {
  const { updateCollection } = useCollections();
  const [formData, setFormData] = useState<CollectionUpdate>({
    name: collection.name,
    description: collection.description || '',
    visibility: collection.visibility
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isOwner = collection.role === 'owner';

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Album name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Album name must be 100 characters or less';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      await updateCollection(collection.collectionId, formData);
      setSnackbar({
        open: true,
        message: 'Album updated successfully',
        severity: 'success'
      });
      onUpdate({ ...collection, ...formData });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update album';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, px: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        <TextField
          label="Album Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          fullWidth
          required
          error={!!errors.name}
          helperText={errors.name}
          disabled={!isOwner || isLoading}
        />

        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          fullWidth
          multiline
          rows={3}
          error={!!errors.description}
          helperText={errors.description}
          disabled={!isOwner || isLoading}
        />

        <FormControl fullWidth disabled={!isOwner || isLoading}>
          <InputLabel>Visibility</InputLabel>
          <Select
            value={formData.visibility}
            label="Visibility"
            onChange={(e) => setFormData({ ...formData, visibility: e.target.value as 'private' | 'public' })}
          >
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="public">Public</MenuItem>
          </Select>
        </FormControl>

        {isOwner && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}

      </Box>

      {isOwner && (
        <>
          <Divider sx={{ my: 4 }} />

          <Box>
            <Typography variant="h6" gutterBottom color="error">
              Danger Zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Deleting an album is permanent and cannot be undone. All items will be removed from this album.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isLoading}
            >
              Delete Album
            </Button>
          </Box>

          <DeleteCollectionDialog
            open={deleteDialogOpen}
            collection={collection}
            onClose={() => setDeleteDialogOpen(false)}
            onDelete={onDelete}
          />
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
